/**
 * duck-rifle.js — the Duck Gallery's cork rifle (GDD 6.5.1).
 *
 * Split out of `duck-gallery.js` because the weapon is a self-contained unit:
 * it owns its body, its dock pose, its muzzle flash, and — the load-bearing
 * part — the decision of where a shot actually goes on each platform.
 *
 *   VR      — the rifle is grabbable and the ray leaves the muzzle along the
 *             barrel, so where you point is where you shoot.
 *   Desktop — mouse-look aims and the rifle is docked to the camera. The ray
 *             still leaves the muzzle, but it is aimed at whatever the
 *             centre-screen crosshair covers, so the 17 cm of parallax between
 *             eye and barrel can never put the shot beside the crosshair.
 *
 * @module stalls/duck-rifle
 */

import * as THREE from 'three';
import { GROUP, makeGroups } from '../constants.js';
import * as factory from '../assets3d/factory.js';
import * as physics from '../core/physics.js';
import { input } from '../core/input.js';
import { grab } from '../core/grab.js';
import { engine } from '../core/engine.js';

/**
 * Where the rifle sits relative to the camera on desktop, in camera space.
 * Far enough forward that the 0.52 m barrel reads as a held rifle rather than
 * filling the bottom-right quarter of the frame.
 */
const DOCK = new THREE.Vector3(0.19, -0.21, -0.42);
/** Membership without a filter: the rifle collides with nothing. */
const GROUPS = makeGroups(GROUP.HAND, 0);
const FORWARD = new THREE.Vector3(0, 0, -1);

export class CorkRifle {
  /**
   * @param {import('./framework.js').StallBase} stall Owner — supplies spawn().
   * @param {{ range: number, flashTime: number }} cfg
   */
  constructor(stall, cfg) {
    this.stall = stall;
    this.cfg = cfg;
    /** @type {import('../core/physics.js').BodyHandle|null} */ this.handle = null;
    /** @type {THREE.Mesh|null} */ this.flashMesh = null;
    this._flashT = 0;

    this._v = new THREE.Vector3();
    this._v2 = new THREE.Vector3();
    this._q = new THREE.Quaternion();
  }

  /** Build the flash quad. Call from the stall's `onBuild`. */
  build(container) {
    // One reused quad beats allocating a mesh on every trigger pull.
    this.flashMesh = new THREE.Mesh(
      new THREE.SphereGeometry(0.04, 8, 6),
      new THREE.MeshBasicMaterial({ color: 0xffe08a, transparent: true, opacity: 0.95 }),
    );
    this.flashMesh.visible = false;
    container.add(this.flashMesh);
  }

  /** Spawn the rifle body. Call from the stall's `setupRound`. */
  spawn() {
    const mesh = factory.createRifle();
    this.handle = this.stall.spawn(mesh, { x: 0.3, y: 1.28, z: 0.42 }, {
      type: 'kinematic', shape: 'box',
      halfExtents: { x: 0.05, y: 0.06, z: 0.33 },
      groups: GROUPS,
    });
    this.handle.userData.rifle = true;
    if (input.isVR) this.registerGrab();
    return this.handle;
  }

  /**
   * Make the rifle grabbable. VR only: on desktop the rifle is docked to the
   * camera, and letting the mouse grab it would hand mouse motion to the grab
   * gesture and take mouse-look — and therefore aiming — away.
   */
  registerGrab() {
    if (!this.handle || this.handle.removed) return;
    grab.register(this.handle, {
      kind: 'rifle',
      holdOffset: this.handle.mesh?.userData?.holdOffset || null,
    });
  }

  /** @returns {boolean} Is a hand holding the rifle? */
  held() {
    return grab.heldBy('right') === this.handle || grab.heldBy('left') === this.handle;
  }

  /** Dock the rifle to the camera and decay the muzzle flash. */
  update(dt) {
    if (this._flashT > 0) {
      this._flashT -= dt;
      if (this.flashMesh) this.flashMesh.visible = this._flashT > 0;
    }
    if (!this.handle || this.handle.removed) return;
    if (input.isVR && this.held()) return; // the hand owns the pose

    const cam = engine.camera;
    if (!cam) return;
    cam.updateWorldMatrix(true, false);
    cam.getWorldQuaternion(this._q);
    cam.getWorldPosition(this._v);
    this._v.add(this._v2.copy(DOCK).applyQuaternion(this._q));
    this.handle.setPosition(this._v);
    this.handle.setRotation(this._q);
  }

  /** @returns {THREE.Vector3|null} A fresh world position of the barrel tip. */
  muzzle() {
    const anchor = this.handle && !this.handle.removed
      ? this.handle.mesh?.userData?.muzzle
      : null;
    if (!anchor) {
      return engine.camera ? engine.camera.getWorldPosition(new THREE.Vector3()) : null;
    }
    anchor.updateWorldMatrix(true, false);
    return anchor.getWorldPosition(new THREE.Vector3());
  }

  /**
   * The ray a trigger pull sends out.
   * @returns {{origin: THREE.Vector3, dir: THREE.Vector3}|null}
   */
  aimRay() {
    const origin = this.muzzle();
    if (!origin) return null;

    if (input.isVR && this.held()) {
      const anchor = this.handle.mesh.userData.muzzle;
      const dir = FORWARD.clone()
        .applyQuaternion(anchor.getWorldQuaternion(new THREE.Quaternion()));
      return { origin, dir };
    }

    const cam = engine.camera;
    if (!cam) return null;
    const camPos = cam.getWorldPosition(new THREE.Vector3());
    const camDir = FORWARD.clone()
      .applyQuaternion(cam.getWorldQuaternion(new THREE.Quaternion()));
    const look = physics.raycast(camPos, camDir, this.cfg.range, { exclude: this.handle });
    const point = look ? look.point : camPos.addScaledVector(camDir, this.cfg.range);
    return { origin, dir: point.sub(origin).normalize() };
  }

  /**
   * Aim from the barrel at an explicit world point.
   * @param {THREE.Vector3} point
   * @returns {{origin: THREE.Vector3, dir: THREE.Vector3}|null}
   */
  aimAt(point) {
    const origin = this.muzzle();
    if (!origin) return null;
    return { origin, dir: point.clone().sub(origin).normalize() };
  }

  /** Pop the muzzle flash at a world position. */
  flash(origin) {
    if (!this.flashMesh || !origin) return;
    this.flashMesh.position.copy(origin);
    this.flashMesh.visible = true;
    this._flashT = this.cfg.flashTime;
  }

  /** Release grab registration. The body itself is removed by stall teardown. */
  dispose() {
    if (this.handle) grab.unregister(this.handle);
    this.handle = null;
    this.flashMesh = null;
    this._flashT = 0;
  }
}
